/**
 * Browser-side handler for /api/sigs/* requests.
 * Mirrors the Vite dev server middleware (vite-middleware/sigsInterceptor.ts)
 * but runs entirely in the browser, calling Substrate via the WorkflowSvc
 * proxy (to avoid browser CORS restrictions).
 *
 * Used in production builds only. Dev mode still uses the Vite middleware.
 */
import { config } from '../config';
import type { MetadataCacheEntry } from '../types/sigs';
import { getFriendlyAgentName } from '../utils/agentNameUtils.js';

import {
  dbGetMetadataCache,
  dbGetTimelineCache,
  dbListPayloads,
  dbSaveMetadataEntries,
  dbSavePayload,
  dbSaveTimelineCache,
} from './sigsDb';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function jsonResponse(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

async function gunzipBase64(base64: string): Promise<string> {
  const binary = atob(base64);
  const bytes = Uint8Array.from({ length: binary.length }, (_, i) =>
    binary.charCodeAt(i),
  );
  const ds = new DecompressionStream('gzip');
  const writer = ds.writable.getWriter();
  const reader = ds.readable.getReader();

  // Write and read concurrently to avoid backpressure deadlock.
  // If the decompressed output is large, writer.close() can stall waiting
  // for the readable to be drained — so both sides must run in parallel.
  const chunks: Uint8Array[] = [];
  await Promise.all([
    writer.write(bytes).then(() => writer.close()),
    (async () => {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) chunks.push(value);
      }
    })(),
  ]);
  const byteLength = chunks.reduce((acc, c) => acc + c.length, 0);
  const result = new Uint8Array(byteLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return new TextDecoder().decode(result);
}

// ---------------------------------------------------------------------------
// Substrate API calls (via the WorkflowSvc proxy to avoid browser CORS restrictions)
// ---------------------------------------------------------------------------

// WorkflowSvc proxies POST /Substrate/explore → Substrate explore endpoint server-side.
const EXPLORE_URL = `${config.apiBaseUrl}/SubstrateProxy/explore`;

// Split a signals time-range into sub-windows of at most this many hours each.
// The CSS signals service ignores $top/$select, so a wide window can return an
// unbounded full-payload response that OOMs or exceeds the gateway timeout
// server-side (surfacing to users as a 504/502). Querying one narrow window at a
// time bounds both the payload size and the per-request latency. 24 = seven
// 1-day requests for the default 7-day window; lower to 12 for 12-hour chunks.
const SIGNAL_QUERY_CHUNK_HOURS = 12;
const SIGNAL_REFRESH_OVERLAP_MS = 10 * 60 * 1000;

// Tile [startMs, endMs] into <= chunkHours windows, newest-first.
function chunkTimeWindows(
  startMs: number,
  endMs: number,
  chunkHours: number,
): { s: string; e: string }[] {
  const chunkMs = chunkHours * 60 * 60 * 1000;
  const windows: { s: string; e: string }[] = [];
  let hi = endMs;
  while (hi > startMs) {
    const lo = Math.max(startMs, hi - chunkMs);
    windows.push({
      s: new Date(lo).toISOString(),
      e: new Date(hi).toISOString(),
    });
    hi = lo;
  }
  // Guard against a zero/negative range producing no windows.
  if (windows.length === 0) {
    windows.push({
      s: new Date(startMs).toISOString(),
      e: new Date(endMs).toISOString(),
    });
  }
  return windows;
}

interface SubstrateSignal {
  CV?: string;
  Id?: string;
  Item?: { ItemId?: string; [key: string]: unknown };
  [key: string]: unknown;
}

interface SubstratePageResponse {
  value?: SubstrateSignal[];
  '@odata.nextLink'?: string;
  [key: string]: unknown;
}

async function fetchPageViaExplore(
  substrateUrl: string,
  authHeader: string,
  substrateAuthHeader?: string,
): Promise<SubstratePageResponse> {
  const headers: Record<string, string> = {
    Authorization: authHeader,
    'Content-Type': 'application/json',
    Accept: 'application/json, text/plain, */*',
  };
  if (substrateAuthHeader) {
    headers['X-Substrate-Authorization'] = substrateAuthHeader;
  }
  const res = await fetch(EXPLORE_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      Url: substrateUrl,
      Method: 'GET',
      Payload: '',
      Headers: {
        Accept: 'application/json',
        Prefer: 'exchange.behavior="SignalAccessV2,OpenComplexTypeExtensions"',
      },
    }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(
      `Substrate API returned ${res.status} ${res.statusText}${body ? `: ${body}` : ''}`,
    );
  }
  return res.json() as Promise<SubstratePageResponse>;
}

async function fetchFullSignal(
  signal: SubstrateSignal,
  authHeader: string,
  substrateAuthHeader?: string,
): Promise<unknown> {
  const signalId = signal.Id;
  if (!signalId) return signal;

  const url = `https://substrate.office.com/api/v2.0/me/signals('${signalId}')`;
  const headers: Record<string, string> = {
    Authorization: authHeader,
    'Content-Type': 'application/json',
    Accept: 'application/json, text/plain, */*',
  };
  if (substrateAuthHeader) {
    headers['X-Substrate-Authorization'] = substrateAuthHeader;
  }
  const res = await fetch(EXPLORE_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      Url: url,
      Method: 'GET',
      Payload: '',
      Headers: {
        Accept: 'application/json',
        Prefer: 'exchange.behavior="SignalAccessV2,OpenComplexTypeExtensions"',
      },
    }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(
      `Substrate API returned ${res.status} ${res.statusText}${body ? `: ${body}` : ''}`,
    );
  }

  const fullSignal = (await res.json()) as Record<string, unknown>;

  // Decompress CompressedData if present
  const customProps = fullSignal.CustomProperties as
    | Record<string, unknown>
    | undefined;
  if (customProps && typeof customProps.CompressedData === 'string') {
    try {
      const decompressed = await gunzipBase64(customProps.CompressedData);
      customProps.CompressedData = JSON.parse(decompressed) as unknown;
    } catch {
      // leave as-is if decompression fails
    }
  }

  return fullSignal;
}

function signalMatchesIdentifier(
  signal: SubstrateSignal,
  identifier: string,
): boolean {
  if (signal.Id === identifier || signal.CV === identifier) return true;
  const itemId = signal.Item?.ItemId;
  return itemId === identifier;
}

async function findSignalByIdentifier(
  identifier: string,
  hoursBack: number,
  authHeader: string,
  substrateAuthHeader?: string,
): Promise<SubstrateSignal | null> {
  const now = new Date();
  const start = new Date(now.getTime() - hoursBack * 60 * 60 * 1000);

  const candidates: SubstrateSignal[] = [];

  // Query one narrow time-window at a time to bound each /explore request's
  // payload and latency (CSS returns full payloads and ignores $top).
  for (const { s, e } of chunkTimeWindows(
    start.getTime(),
    now.getTime(),
    SIGNAL_QUERY_CHUNK_HOURS,
  )) {
    const filter =
      `((StartTime ge ${s} and StartTime le ${e})` +
      ` or (EndTime ge ${s} and EndTime le ${e})` +
      ` or (StartTime le ${s} and EndTime ge ${e}))` +
      ` and SignalType eq 'LlmPromptResponse'`;

    let nextUrl: string | null =
      `https://substrate.office.com/api/v2.0/me/signals` +
      `?$filter=${filter}` +
      `&$top=200`;

    while (nextUrl) {
      const page = await fetchPageViaExplore(
        nextUrl,
        authHeader,
        substrateAuthHeader,
      );
      const signals = page.value ?? [];
      for (const sig of signals) {
        if (signalMatchesIdentifier(sig, identifier)) {
          candidates.push(sig);
        }
      }
      nextUrl = page['@odata.nextLink'] ?? null;
    }
  }

  // The time-range listing already carries each candidate's CompressedData (CSS returns the full
  // payload in the $filter query — there is no by-id endpoint), so resolve the match from it directly
  // and decompress inline. Only exact identifier matches are candidates; scenario names must not
  // override signal identity. A per-candidate full fetch is only a
  // safety net for the rare case where the listing omitted CompressedData.
  for (const sig of candidates) {
    try {
      const listingProps = sig.CustomProperties as
        | Record<string, unknown>
        | undefined;
      let customProps: Record<string, unknown> | undefined;

      if (listingProps && typeof listingProps.CompressedData === 'string') {
        const decompressed = await gunzipBase64(listingProps.CompressedData);
        customProps = {
          ...listingProps,
          CompressedData: JSON.parse(decompressed) as unknown,
        };
      } else if (
        listingProps &&
        typeof listingProps.CompressedData === 'object' &&
        listingProps.CompressedData !== null
      ) {
        customProps = listingProps;
      } else {
        const fullSignal = (await fetchFullSignal(
          sig,
          authHeader,
          substrateAuthHeader,
        )) as Record<string, unknown>;
        customProps = fullSignal.CustomProperties as
          | Record<string, unknown>
          | undefined;
      }

      if (
        !customProps ||
        typeof customProps.CompressedData !== 'object' ||
        customProps.CompressedData === null
      ) {
        continue;
      }
      return { ...sig, CustomProperties: customProps };
    } catch {
      // skip signals that fail to decompress/fetch
    }
  }

  return null;
}

interface RecentSignalEntry {
  itemId: string;
  signalId: string;
  cv: string;
  startTime: string;
  creationTime?: string;
  signalType: string;
  appName?: string;
  agentName?: string;
  promptPreview?: string;
}

function toMetadataEntries(entries: RecentSignalEntry[]): MetadataCacheEntry[] {
  return entries
    .filter((entry) => entry.signalId)
    .map((entry) => ({
      itemId: entry.itemId,
      signalId: entry.signalId,
      cv: entry.cv,
      startTime: entry.startTime,
      creationTime: entry.creationTime,
      appName: entry.appName,
      promptPreview: entry.promptPreview,
    }));
}

/** Extract agent identity fields from a full signal's decompressed CustomProperties. */
function extractExecutionInfo(fullSignal: Record<string, unknown>): {
  appScenario?: string;
  productEntryPoint?: string;
  clientAppName?: string;
} {
  const customProps = fullSignal.CustomProperties as
    | Record<string, unknown>
    | undefined;
  if (!customProps || typeof customProps.CompressedData !== 'object') return {};
  const data = customProps.CompressedData as Record<string, unknown>;
  const execCtx = data?.ExecutionContext as Record<string, unknown> | undefined;
  return {
    appScenario: execCtx?.AppScenario as string | undefined,
    productEntryPoint: execCtx?.ProductEntryPoint as string | undefined,
    clientAppName: execCtx?.ClientAppName as string | undefined,
  };
}

/** Extract the user prompt from a full signal's decompressed ExecutionEvents. */
function extractPromptPreview(
  fullSignal: Record<string, unknown>,
): string | undefined {
  const customProps = fullSignal.CustomProperties as
    | Record<string, unknown>
    | undefined;
  if (!customProps || typeof customProps.CompressedData !== 'object')
    return undefined;
  const data = customProps.CompressedData as Record<string, unknown>;
  const events = data?.ExecutionEvents as Record<string, unknown>[] | undefined;
  if (!Array.isArray(events)) return undefined;

  // Prefer CopilotChatExecutionEvent.UserPrompt (Augloop signals)
  const chatEvent = events.find(
    (e) => e.EventType === 'CopilotChatExecutionEvent',
  );
  const prompt = chatEvent?.UserPrompt;
  if (typeof prompt === 'string' && prompt.trim()) {
    return prompt.slice(0, 80).replace(/\s+/g, ' ').trim();
  }

  // Fallback: UserUtteranceExecutionEvent.MessageText (Sydney signals)
  const utteranceEvent = events.find(
    (e) => e.EventType === 'UserUtteranceExecutionEvent',
  );
  const messageText = utteranceEvent?.MessageText;
  if (typeof messageText === 'string' && messageText.trim()) {
    return messageText.slice(0, 80).replace(/\s+/g, ' ').trim();
  }

  return undefined;
}

async function fetchRecentSignals(
  authHeader: string,
  days: number,
  substrateAuthHeader?: string,
  sinceTime?: string,
): Promise<RecentSignalEntry[]> {
  const now = new Date();
  const start = sinceTime
    ? new Date(sinceTime)
    : new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

  // Collect all signals from the listing, one narrow time-window at a time so
  // each /explore request stays small and fast (CSS ignores $top/$select and
  // returns full payloads, so a wide window can OOM/time out server-side).
  const allSignals: SubstrateSignal[] = [];

  outer: for (const { s, e } of chunkTimeWindows(
    start.getTime(),
    now.getTime(),
    SIGNAL_QUERY_CHUNK_HOURS,
  )) {
    let nextUrl: string | null =
      `https://substrate.office.com/api/v2.0/me/signals` +
      `?$filter=(StartTime ge ${s} and StartTime le ${e} and SignalType eq 'LlmPromptResponse')` +
      `&$orderby=StartTime desc&$top=200` +
      `&$select=Id,CV,CreationTime,StartTime,EndTime,Item,Application,SignalType,CustomProperties`;

    while (nextUrl) {
      const page = await fetchPageViaExplore(
        nextUrl,
        authHeader,
        substrateAuthHeader,
      );
      const signals = page.value ?? [];
      for (const signal of signals) {
        const item = signal.Item as { ItemId?: string } | undefined;
        if (item?.ItemId) {
          allSignals.push(signal);
        }
      }
      nextUrl = page['@odata.nextLink'] ?? null;
      if (allSignals.length >= 500) break outer;
    }
  }

  const uniqueSignals = new Map<string, SubstrateSignal>();
  for (const signal of allSignals) {
    const itemId = (signal.Item as { ItemId?: string }).ItemId!;
    const startTime =
      typeof signal.StartTime === 'string' ? signal.StartTime : '';
    const signalId = signal.Id;
    const key =
      typeof signalId === 'string' && signalId.length > 0
        ? signalId
        : `${itemId}\u0000${signal.CV ?? ''}\u0000${startTime}`;
    if (!uniqueSignals.has(key)) {
      uniqueSignals.set(key, signal);
    }
  }

  // Enrich each signal with AppScenario and prompt preview.
  // If the listing included CustomProperties (compressed), decompress inline.
  // Otherwise fall back to individual full-signal fetches.
  const signals = [...uniqueSignals.values()];
  const results: RecentSignalEntry[] = [];
  const CONCURRENCY = 10;

  for (let i = 0; i < signals.length; i += CONCURRENCY) {
    const batch = signals.slice(i, i + CONCURRENCY);
    const enriched = await Promise.allSettled(
      batch.map(async (signal) => {
        const itemId = (signal.Item as { ItemId?: string }).ItemId!;
        const app = signal.Application as Record<string, unknown> | undefined;
        const appName =
          (app?.AppName as string | undefined) ??
          (app?.WorkloadName as string | undefined) ??
          undefined;

        let agentName: string | undefined;
        let promptPreview: string | undefined;

        // Try to use CustomProperties from the listing response directly
        const inlineCustomProps = signal.CustomProperties as
          | Record<string, unknown>
          | undefined;
        let fullSignalData: Record<string, unknown> | undefined;

        if (
          inlineCustomProps &&
          typeof inlineCustomProps.CompressedData === 'string'
        ) {
          // Listing returned compressed data as base64 — decompress it
          try {
            const decompressed = await gunzipBase64(
              inlineCustomProps.CompressedData,
            );
            inlineCustomProps.CompressedData = JSON.parse(
              decompressed,
            ) as unknown;
            fullSignalData = signal as unknown as Record<string, unknown>;
          } catch {
            // Decompression failed, fall back to individual fetch
          }
        } else if (
          inlineCustomProps &&
          typeof inlineCustomProps.CompressedData === 'object'
        ) {
          // Already decompressed (shouldn't happen on listing but handle it)
          fullSignalData = signal as unknown as Record<string, unknown>;
        }

        // Fall back to individual fetch if listing didn't include CustomProperties
        if (!fullSignalData) {
          try {
            fullSignalData = (await fetchFullSignal(
              signal,
              authHeader,
              substrateAuthHeader,
            )) as Record<string, unknown>;
          } catch {
            // If full fetch fails, still show entry without agent name
          }
        }

        if (fullSignalData) {
          const { appScenario, productEntryPoint, clientAppName } =
            extractExecutionInfo(fullSignalData);
          agentName = getFriendlyAgentName(
            appScenario,
            productEntryPoint,
            clientAppName,
          );
          promptPreview = extractPromptPreview(fullSignalData);
        }

        return {
          itemId,
          signalId: signal.Id ?? '',
          cv: signal.CV ?? itemId,
          startTime:
            (signal.StartTime as string | undefined) ??
            new Date().toISOString(),
          creationTime: signal.CreationTime as string | undefined,
          signalType:
            (signal.SignalType as string | undefined) ?? 'LlmPromptResponse',
          appName,
          agentName,
          promptPreview,
        } satisfies RecentSignalEntry;
      }),
    );

    for (const result of enriched) {
      if (result.status === 'fulfilled') {
        results.push(result.value);
      }
    }
  }

  return results;
}

async function proxyToSubstrate(
  identifier: string,
  authHeader: string,
  substrateAuthHeader?: string,
): Promise<unknown> {
  if (!authHeader) {
    throw new Error(
      'No Authorization header provided. Sign in to the webapp to access Substrate signals.',
    );
  }
  // We scope to last 7 days because that is all the api allows
  const match = await findSignalByIdentifier(
    identifier,
    168,
    authHeader,
    substrateAuthHeader,
  );
  if (match) {
    // findSignalByItemId resolves the full signal from the listing (CompressedData already
    // decompressed inline), so no additional by-id fetch is needed.
    return match;
  }

  throw new Error(
    `No signal found with identifier "${identifier}" in the last 7 days.`,
  );
}

// ---------------------------------------------------------------------------
// Main route dispatcher — mirrors sigsInterceptor.ts route handling
// ---------------------------------------------------------------------------

export async function handleSigsRequest(request: Request): Promise<Response> {
  const url = new URL(request.url, window.location.href);
  // Strip the /api/sigs prefix to get the sub-path
  const subPath = url.pathname.replace(/^\/api\/sigs/, '') || '/';

  // POST /api/sigs — store incoming SIGS payload (from local AugLoop emission or Load from JSON)
  if (request.method === 'POST' && (subPath === '/' || subPath === '')) {
    try {
      const payload = (await request.json()) as Record<string, unknown>;
      // Handle both single signal and {value:[...]} wrapper
      const signal: Record<string, unknown> = Array.isArray(payload.value)
        ? ((payload.value as Record<string, unknown>[])[0] ?? payload)
        : payload;
      const item = signal.Item as Record<string, unknown> | undefined;
      const itemId =
        (item?.ItemId as string | undefined) ??
        (signal.CV as string | undefined) ??
        '';
      if (!itemId)
        return jsonResponse(
          {
            error: 'Cannot determine signal identifier (no Item.ItemId or CV)',
          },
          400,
        );
      await dbSavePayload(itemId, payload);
      return jsonResponse({ ok: true, itemId });
    } catch (e) {
      return jsonResponse({ error: String(e) }, 400);
    }
  }

  // GET /api/sigs/local — list stored payloads
  if (request.method === 'GET' && subPath === '/local') {
    const entries = await dbListPayloads();
    return jsonResponse(entries);
  }

  // GET /api/sigs/recent[?days=N] — recent LlmPromptResponse signals from Substrate
  if (request.method === 'GET' && subPath.startsWith('/recent')) {
    const authHeader = request.headers.get('Authorization') ?? '';
    const substrateAuthHeader =
      request.headers.get('X-Substrate-Authorization') ?? undefined;
    if (!authHeader) {
      return jsonResponse(
        {
          error:
            'No Authorization header. Sign in to the webapp to access Substrate signals.',
        },
        401,
      );
    }
    const days = Math.min(
      Math.max(parseInt(url.searchParams.get('days') ?? '1', 10) || 1, 1),
      30,
    );
    const skipCache = url.searchParams.get('refresh') === '1';
    const cacheKey = `recent_${days}`;

    // Serve from cache if available and not explicitly refreshing
    if (!skipCache) {
      const cached = await dbGetTimelineCache(cacheKey);
      if (cached) {
        const cachedEntries = cached.entries as RecentSignalEntry[];
        const metadataEntries = toMetadataEntries(cachedEntries);
        if (metadataEntries.length > 0) {
          await dbSaveMetadataEntries(metadataEntries);
        }
        return jsonResponse(cached.entries);
      }
    }

    try {
      const refreshBoundary = Date.now();
      // Refresh the interval since the previous update, with overlap for
      // signals that become queryable after their interaction start time.
      let data: RecentSignalEntry[];
      if (skipCache) {
        const cached = await dbGetTimelineCache(cacheKey);
        if (cached) {
          const refreshStart = new Date(
            cached.refreshBoundary - SIGNAL_REFRESH_OVERLAP_MS,
          ).toISOString();
          const newEntries = await fetchRecentSignals(
            authHeader,
            days,
            substrateAuthHeader,
            refreshStart,
          );
          // Merge by signal identity so multiple signals for one interaction
          // remain visible while overlap results do not duplicate entries.
          const seen = new Set<string>();
          data = [];
          for (const entry of [
            ...newEntries,
            ...(cached.entries as RecentSignalEntry[]),
          ]) {
            const key =
              entry.signalId ||
              `${entry.itemId}\u0000${entry.cv}\u0000${entry.startTime}`;
            if (!seen.has(key)) {
              seen.add(key);
              data.push(entry);
            }
          }
          const retentionStart = refreshBoundary - days * 24 * 60 * 60 * 1000;
          data = data.filter(
            (entry) => Date.parse(entry.startTime) >= retentionStart,
          );
          data.sort((a, b) => b.startTime.localeCompare(a.startTime));
        } else {
          data = await fetchRecentSignals(
            authHeader,
            days,
            substrateAuthHeader,
          );
        }
      } else {
        data = await fetchRecentSignals(authHeader, days, substrateAuthHeader);
      }

      const metadataEntries = toMetadataEntries(data);
      if (metadataEntries.length > 0) {
        await dbSaveMetadataEntries(metadataEntries);
      }
      void dbSaveTimelineCache(cacheKey, data, refreshBoundary);
      return jsonResponse(data);
    } catch (e) {
      return jsonResponse({ error: String(e) }, 502);
    }
  }

  // GET /api/sigs/metadata — return cached signal metadata
  if (request.method === 'GET' && subPath === '/metadata') {
    const cache = await dbGetMetadataCache();
    return jsonResponse(cache);
  }

  // GET /api/sigs/:itemId — fetch by ItemId directly from Substrate
  if (request.method === 'GET' && subPath.length > 1) {
    const itemId = decodeURIComponent(subPath.slice(1));
    try {
      const authHeader = request.headers.get('Authorization') ?? '';
      const substrateAuthHeader =
        request.headers.get('X-Substrate-Authorization') ?? undefined;
      const data = await proxyToSubstrate(
        itemId,
        authHeader,
        substrateAuthHeader,
      );
      return new Response(JSON.stringify(data), {
        status: 200,
        headers: {
          'Content-Type': 'application/json',
          'X-Sigs-Source': 'substrate',
        },
      });
    } catch (e) {
      return jsonResponse({ error: String(e) }, 502);
    }
  }

  return jsonResponse({ error: 'Not found' }, 404);
}
